import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import { UpdateSupplierModal } from "@/components/suppliers/UpdateSupplierModal";
import { AddSupplierServiceModal } from "@/components/suppliers/AddSupplierServiceModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StarRating } from "@/components/ui/star-rating";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Pencil,
  Loader2,
  Truck,
  Phone,
  Mail,
  FileText,
  Calendar,
  Star,
  CreditCard,
  TrendingUp,
  Wallet,
  CalendarClock,
  DollarSign,
  Eye,
  Plus,
  Tag,
  Settings2,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import {
  supplierService,
  ESupplierStatus,
  SupplierService,
  SupplierPricePlan,
  CreateSupplierPricePlanRequest,
} from "@/services/supplier";
import { streamingServiceApi, PricePlanRequest } from "@/services/streaming-service";
import { ESubscriptionStatus, ESubType } from "@/services/subscription";
import { EPaymentStatus, EPaymentMethod } from "@/services/payment";
import { formatCurrency } from "@/lib/utils";

function pricePlanTypeLabel(type: string): string {
  return type === "FULL_ACCOUNT" ? "Cuenta completa" : "Slot";
}

const buildEmptyPlanForm = (): CreateSupplierPricePlanRequest => ({
  name: "",
  price: 0,
  duration: 30,
  pricePlanType: "SLOT",
  status: ESupplierStatus.ACTIVE,
  isDefault: false,
  streamingServiceId: undefined,
});

type PlanFormMode =
  | { type: "create"; serviceId: number }
  | { type: "edit"; planId: number };

function getPaymentStatusLabel(status: EPaymentStatus): string {
  const labels: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "Completado",
    [EPaymentStatus.PENDING]: "Pendiente",
    [EPaymentStatus.CANCELLED]: "Cancelado",
    [EPaymentStatus.REFUNDED]: "Reembolsado",
  };
  return labels[status] || status;
}

function getPaymentStatusColor(status: EPaymentStatus): string {
  const colors: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "bg-green-100 text-green-800",
    [EPaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800",
    [EPaymentStatus.CANCELLED]: "bg-red-100 text-red-800",
    [EPaymentStatus.REFUNDED]: "bg-blue-100 text-blue-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
}

function getPaymentMethodLabel(method: EPaymentMethod): string {
  const labels: Record<EPaymentMethod, string> = {
    [EPaymentMethod.CASH]: "Efectivo",
    [EPaymentMethod.TRANSFER]: "Transferencia",
    [EPaymentMethod.NEQUI]: "Nequi",
    [EPaymentMethod.DAVIPLATA]: "Daviplata",
    [EPaymentMethod.BANCOLOMBIA]: "Bancolombia",
    [EPaymentMethod.PAYPAL]: "PayPal",
    [EPaymentMethod.BINANCE]: "Binance",
    [EPaymentMethod.YAPE]: "Yape",
  };
  return labels[method] || method;
}

export default function SupplierDetail() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [expandedServiceId, setExpandedServiceId] = useState<number | null>(null);
  const [planFormMode, setPlanFormMode] = useState<PlanFormMode | null>(null);
  const [planFormData, setPlanFormData] = useState<CreateSupplierPricePlanRequest>(
    buildEmptyPlanForm(),
  );

  const numericSupplierId = Number(supplierId);

  const {
    data: supplierResponse,
    isLoading: isLoadingSupplier,
    error,
  } = useQuery({
    queryKey: ["supplier", numericSupplierId],
    queryFn: () => supplierService.getOne(numericSupplierId),
    enabled: !!supplierId,
  });

  const supplier = supplierResponse?.data;
  const subscriptions = supplier?.subscriptions || [];
  const payments = supplier?.payments || [];
  const supplierServices = supplier?.supplierServices || [];

  const { data: pricePlansResponse, isLoading: isLoadingPricePlans } =
    useQuery({
      queryKey: ["supplier-price-plans", numericSupplierId],
      queryFn: () => supplierService.getPricePlans(numericSupplierId),
      enabled: !!supplierId,
    });

  const pricePlans = pricePlansResponse?.data || [];

  const toggleServiceStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ESupplierStatus }) =>
      supplierService.updateService(numericSupplierId, id, { status }),
    onSuccess: () => {
      toast.success("Servicio del proveedor actualizado exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["supplier", numericSupplierId],
      });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const createPricePlanMutation = useMutation({
    mutationFn: (serviceId: number) =>
      supplierService.createPricePlan(numericSupplierId, {
        ...planFormData,
        streamingServiceId: Number(serviceId),
      }),
    onSuccess: () => {
      toast.success("Plan de precio registrado exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["supplier-price-plans", numericSupplierId],
      });
      setPlanFormMode(null);
      setPlanFormData(buildEmptyPlanForm());
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const updatePricePlanMutation = useMutation({
    mutationFn: (planId: number) =>
      streamingServiceApi.updatePricePlan(
        planId,
        {duration: planFormData.duration, isDefault: planFormData.isDefault, name: planFormData.name, price: planFormData.price, pricePlanType: planFormData.pricePlanType},
      ),
    onSuccess: () => {
      toast.success("Plan de precio actualizado exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["supplier-price-plans", numericSupplierId],
      });
      setPlanFormMode(null);
      setPlanFormData(buildEmptyPlanForm());
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const isSavingPlan =
    createPricePlanMutation.isPending || updatePricePlanMutation.isPending;

  const toggleServicePanel = (serviceId: number) => {
    setExpandedServiceId((prev) => (prev === serviceId ? null : serviceId));
    setPlanFormMode(null);
  };

  const openCreatePlanForm = (serviceId: number) => {
    setPlanFormData(buildEmptyPlanForm());
    setPlanFormMode({ type: "create", serviceId });
  };

  const openEditPlanForm = (plan: SupplierPricePlan) => {
    setPlanFormData({
      name: plan.name,
      price: Number(plan.price),
      duration: plan.duration,
      pricePlanType: plan.pricePlanType,
      status: plan.status,
      isDefault: plan.isDefault,
      streamingServiceId: plan.streamingServiceId,
    });
    setPlanFormMode({ type: "edit", planId: plan.id });
  };

  const handlePlanFormSubmit = () => {
    if (!planFormMode) return;
    if (!planFormData.name.trim()) {
      toast.error("El nombre del plan es requerido");
      return;
    }
    if (!planFormData.duration || planFormData.duration <= 0) {
      toast.error("La duración debe ser mayor a 0");
      return;
    }
    if (planFormMode.type === "create") {
      createPricePlanMutation.mutate(planFormMode.serviceId);
    } else {
      updatePricePlanMutation.mutate(planFormMode.planId);
    }
  };

  const renderPlanForm = (onCancel: () => void, submitLabel: string) => (
    <div className="space-y-3 rounded-lg border border-primary/30 bg-muted/10 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">
          {planFormMode?.type === "edit" ? "Editar plan" : "Nuevo plan"}
        </p>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onCancel}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="plan-name" className="text-xs">
          Nombre <span className="text-destructive">*</span>
        </Label>
        <Input
          id="plan-name"
          className="h-8 text-sm"
          placeholder="Combo, 30 días"
          value={planFormData.name}
          onChange={(e) =>
            setPlanFormData((prev) => ({ ...prev, name: e.target.value }))
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="plan-price" className="text-xs">
            Precio <span className="text-destructive">*</span>
          </Label>
          <Input
            id="plan-price"
            type="number"
            step="0.01"
            min={0}
            className="h-8 text-sm"
            value={planFormData.price}
            onChange={(e) =>
              setPlanFormData((prev) => ({
                ...prev,
                price: parseFloat(e.target.value) || 0,
              }))
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-duration" className="text-xs">
            Duración (días) <span className="text-destructive">*</span>
          </Label>
          <Input
            id="plan-duration"
            type="number"
            min={1}
            className="h-8 text-sm"
            value={planFormData.duration}
            onChange={(e) =>
              setPlanFormData((prev) => ({
                ...prev,
                duration: parseInt(e.target.value, 10) || 0,
              }))
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Tipo de plan</Label>
          <Select
            value={planFormData.pricePlanType}
            onValueChange={(val) =>
              setPlanFormData((prev) => ({
                ...prev,
                pricePlanType: val as "SLOT" | "FULL_ACCOUNT",
              }))
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SLOT">Slot</SelectItem>
              <SelectItem value="FULL_ACCOUNT">Cuenta completa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Estado</Label>
          <Select
            value={planFormData.status}
            onValueChange={(val) =>
              setPlanFormData((prev) => ({
                ...prev,
                status: val as ESupplierStatus,
              }))
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ESupplierStatus.ACTIVE}>Activo</SelectItem>
              <SelectItem value={ESupplierStatus.INACTIVE}>
                Inactivo
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5">
        <Label htmlFor="plan-default" className="text-xs">
          Plan por defecto
        </Label>
        <Switch
          id="plan-default"
          checked={planFormData.isDefault}
          onCheckedChange={(checked) =>
            setPlanFormData((prev) => ({ ...prev, isDefault: checked }))
          }
        />
      </div>

      <Button
        className="w-full h-8 text-sm"
        onClick={handlePlanFormSubmit}
        disabled={isSavingPlan}
      >
        {isSavingPlan ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
        ) : null}
        {submitLabel}
      </Button>
    </div>
  );

  const isLoading = isLoadingSupplier;

  const activeSubscriptions = subscriptions.filter(
    (s) =>
      s.status === ESubscriptionStatus.ACTIVE ||
      s.status === ESubscriptionStatus.EXPIRING,
  );

  const monthlyValue = activeSubscriptions.reduce(
    (sum, s) => sum + Number(s.price),
    0,
  );

  const totalPaid = payments
    .filter((p) => p.status === EPaymentStatus.COMPLETED)
    .reduce((sum, p) => Number(sum) + Number(p.amount), 0);

  const nextExpiring = activeSubscriptions.reduce(
    (min, s) => {
      if (!min || s.daysRemaining < min.daysRemaining) return s;
      return min;
    },
    null as (typeof subscriptions)[number] | null,
  );

  if (isLoading) {
    return (
      <DashboardLayout title="Proveedor" subtitle="Cargando...">
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !supplier) {
    return (
      <DashboardLayout title="Proveedor" subtitle="No encontrado">
        <div className="card-elevated p-8 text-center">
          <p className="text-muted-foreground">Proveedor no encontrado</p>
          <Button
            variant="outline"
            className="mt-4 gap-2"
            onClick={() => navigate("/suppliers")}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a proveedores
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={supplier.name}
      subtitle="Detalle del proveedor"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Header actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate("/suppliers")}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setIsUpdateModalOpen(true)}
          >
            <Pencil className="h-4 w-4" />
            Editar proveedor
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Suscripciones Activas
                  </p>
                  <p className="text-2xl font-bold">
                    {activeSubscriptions.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Mensual</p>
                  <p className="text-2xl font-bold">
                    ${formatCurrency(monthlyValue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <Wallet className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Pagado</p>
                  <p className="text-2xl font-bold">
                    ${formatCurrency(totalPaid)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-orange-500/10">
                  <CalendarClock className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Próximo Vencimiento
                  </p>
                  <p className="text-2xl font-bold">
                    {nextExpiring
                      ? nextExpiring.daysRemaining > 0
                        ? `${nextExpiring.daysRemaining} días`
                        : "Vencido"
                      : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main info card */}
        <div className="card-elevated p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Truck className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-semibold text-foreground">
                  {supplier.name}
                </h2>
                <StatusBadge status={supplier.status} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                <p className="text-sm font-medium text-foreground">
                  {supplier.email || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Teléfono</p>
                <p className="text-sm font-medium text-foreground">
                  {supplier.phone || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Star className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">
                  Puntuación
                </p>
                {supplier.score !== undefined && supplier.score !== null ? (
                  <div className="flex items-center gap-2">
                    <StarRating value={supplier.score} size="sm" />
                    <span className="text-sm font-medium text-foreground">
                      {supplier.score} / 10
                    </span>
                  </div>
                ) : (
                  <p className="text-sm font-medium text-foreground">—</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">
                  Fecha de registro
                </p>
                <p className="text-sm font-medium text-foreground">
                  {new Date(supplier.createdAt).toLocaleDateString("es-CO", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            {supplier.updatedAt && (
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    Última actualización
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {new Date(supplier.updatedAt).toLocaleDateString("es-CO", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-start gap-3">
              <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground mb-1">Notas</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {supplier.notes || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Servicios del proveedor */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tag className="h-5 w-5 text-primary" />
                Servicios que Surte
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => setIsAddServiceModalOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Agregar servicio
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingPricePlans ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : supplierServices.length === 0 ? (
              <div className="text-center py-8">
                <Tag className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  Este proveedor aún no tiene servicios asociados
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {supplierServices.map((supplierServiceItem) => {
                  const service = supplierServiceItem.service;
                  const isActive =
                    supplierServiceItem.status === ESupplierStatus.ACTIVE;
                  const servicePlans = pricePlans.filter(
                    (p) => p.streamingServiceId === supplierServiceItem.serviceId,
                  );
                  const cheapestPrice = servicePlans.length
                    ? Math.min(...servicePlans.map((p) => Number(p.price)))
                    : null;
                  const serviceSubscriptionsCount = subscriptions.filter((s) =>
                    s.subscriptionItems?.some(
                      (item) => item.serviceId === supplierServiceItem.serviceId,
                    ),
                  ).length;
                  const isExpanded = expandedServiceId === supplierServiceItem.id;
                  return (
                    <div
                      key={supplierServiceItem.id}
                      className={`flex flex-col gap-3 p-4 rounded-lg border bg-card transition-colors hover:border-primary/40 ${
                        isExpanded ? "sm:col-span-2 lg:col-span-3" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                            style={{
                              backgroundColor: service?.color
                                ? `${service.color}20`
                                : "hsl(var(--muted))",
                            }}
                          >
                            {service?.icon || "📺"}
                          </span>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {service?.name || `Servicio #${supplierServiceItem.serviceId}`}
                            </p>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <StatusBadge status={supplierServiceItem.status} />
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                                <CreditCard className="h-2.5 w-2.5" />
                                {serviceSubscriptionsCount} suscripcion
                                {serviceSubscriptionsCount === 1 ? "" : "es"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Switch
                          checked={isActive}
                          disabled={toggleServiceStatusMutation.isPending}
                          onCheckedChange={(checked) =>
                            toggleServiceStatusMutation.mutate({
                              id: supplierServiceItem.id,
                              status: checked
                                ? ESupplierStatus.ACTIVE
                                : ESupplierStatus.INACTIVE,
                            })
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between text-xs pt-3 border-t border-border/60">
                        <span className="text-muted-foreground">
                          {servicePlans.length === 0
                            ? "Sin planes de precio"
                            : `${servicePlans.length} plan${servicePlans.length === 1 ? "" : "es"} de precio`}
                        </span>
                        {cheapestPrice !== null && (
                          <span className="font-semibold text-foreground">
                            desde ${formatCurrency(cheapestPrice)}
                          </span>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={() => toggleServicePanel(supplierServiceItem.id)}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                        Gestionar planes
                        {isExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5 ml-auto" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 ml-auto" />
                        )}
                      </Button>

                      {isExpanded && (
                        <div className="pt-3 border-t border-border/60 space-y-2">
                          {servicePlans.length === 0 && planFormMode?.type !== "create" ? (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              Aún no hay planes de precio para este servicio
                            </p>
                          ) : (
                            servicePlans.map((plan) =>
                              planFormMode?.type === "edit" &&
                              planFormMode.planId === plan.id ? (
                                <div key={plan.id}>
                                  {renderPlanForm(() => setPlanFormMode(null), "Guardar cambios")}
                                </div>
                              ) : (
                                <div
                                  key={plan.id}
                                  className="flex items-center justify-between gap-2 rounded-md border bg-muted/20 px-2.5 py-2"
                                >
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-xs font-medium truncate">
                                        {plan.name}
                                      </p>
                                      {plan.isDefault && (
                                        <Badge
                                          variant="secondary"
                                          className="shrink-0 text-[9px] px-1.5 py-0"
                                        >
                                          Por defecto
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">
                                      {pricePlanTypeLabel(plan.pricePlanType)} ·{" "}
                                      {plan.duration} días
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-xs font-semibold">
                                      ${formatCurrency(plan.price)}
                                    </span>
                                    <StatusBadge status={plan.status} />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => openEditPlanForm(plan)}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ),
                            )
                          )}

                          {planFormMode?.type === "create" &&
                          planFormMode.serviceId === supplierServiceItem.serviceId ? (
                            renderPlanForm(() => setPlanFormMode(null), "Registrar plan")
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full gap-2 h-8 text-xs"
                              onClick={() =>
                                openCreatePlanForm(supplierServiceItem.serviceId)
                              }
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Agregar plan de precio
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscriptions Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-primary" />
                Suscripciones
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                {subscriptions.length} suscripcion(es)
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No hay suscripciones</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Este proveedor no tiene suscripciones asociadas
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Servicio</TableHead>
                      <TableHead>Cuenta / Slot</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead className="text-center">Días Restantes</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          {subscription.type === ESubType.COMBO ? (
                            <ServiceBadge icon="📦" name={subscription?.combo?.name || "Combo"} />
                          ) : (
                            <ServiceBadge
                              icon={subscription?.subscriptionItems[0]?.service?.icon || "—"}
                              name={subscription?.subscriptionItems[0]?.service?.name || "—"}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">
                              {subscription.subscriptionItems?.[0]?.slot?.name || "Cuenta completa"}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {subscription.type === ESubType.COMBO
                                ? "Múltiples cuentas"
                                : subscription.subscriptionItems?.[0]?.streamingAccount?.email || "-"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(subscription.startDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell>
                          {new Date(subscription.endDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`font-semibold ${
                              subscription.daysRemaining <= 3
                                ? "text-destructive"
                                : subscription.daysRemaining <= 7
                                  ? "text-orange-500"
                                  : "text-foreground"
                            }`}
                          >
                            {subscription.daysRemaining > 0
                              ? subscription.daysRemaining
                              : "Vencido"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${formatCurrency(subscription.price)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={subscription.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-primary" />
                Historial de Pagos
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Total pagado:{" "}
                <span className="font-semibold text-foreground">
                  ${formatCurrency(totalPaid)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No hay pagos registrados</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Los pagos apareceran cuando se registren suscripciones
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.paymentDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${formatCurrency(Number(payment.amount))}
                        </TableCell>
                        <TableCell>
                          {getPaymentMethodLabel(payment.paymentMethod)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}
                          >
                            {getPaymentStatusLabel(payment.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {payment.notes || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {payment.subscriptionId && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                navigate(`/subscriptions/${payment.subscriptionId}`)
                              }
                              title="Ver suscripción"
                            >
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <UpdateSupplierModal
        open={isUpdateModalOpen}
        onOpenChange={setIsUpdateModalOpen}
        supplier={supplier}
      />

      <AddSupplierServiceModal
        open={isAddServiceModalOpen}
        onOpenChange={setIsAddServiceModalOpen}
        supplierId={numericSupplierId}
        existingServices={supplierServices}
      />
    </DashboardLayout>
  );
}
